'use client';
import React, { FC, useEffect, useRef, useState } from 'react';
import Select, {
  MultiValue,
  SingleValue,
  ActionMeta,
  components,
  MultiValueProps,
  ValueContainerProps,
  OptionProps,
  MenuListProps,
} from 'react-select';
import { OptionType, SelectProps } from 'components/types';
import '../../styles/components/select2.css';
import { useField, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

interface FormValues {
  setFieldValue: (field: string, value: any) => void;
}

const MinthrSelect: FC<SelectProps> = ({
  isMulti = false,
  options,
  name = '',
  label,
  className = '',
  IsDisabled,
  onchange,
  borderNone = false,
  value: initialValue,
  hidden = false,
  Control,
  Selectstyle,
  appendToBody,
  placement = 'auto',
  selectClass = '',
  placeholder = 'Search',
  isSearchable = true,
  setFirstValue,
  isClearable = false,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<
    MultiValue<OptionType> | SingleValue<OptionType> | null
  >(initialValue || null);
  const [labelWidth, setLabelWidth] = useState(0);
  const [focused, setFocused] = useState(false);
  const [isPanelOpen, setIsOpenPanel] = useState<boolean>(false);
  const menuListRef = useRef<HTMLDivElement>(null);

  const formikField = name ? useField(name) : null;
  const meta = formikField ? formikField[1] : { touched: false, error: null };
  const { setFieldValue } = name
    ? useFormikContext<FormValues>()
    : { setFieldValue: () => {} };

  const labelRef = useRef<HTMLLabelElement>(null);

  const uniqueOptions = React.useMemo(() => {
    return options.filter(
      (option, index, self) =>
        self.findIndex((opt) => opt.value === option.value) === index,
    );
  }, [options]);

  useEffect(() => {
    if (!labelRef.current) return;

    const updateLabelWidth = () => {
      if (labelRef.current) {
        setLabelWidth(labelRef.current.offsetWidth);
      }
    };
    updateLabelWidth();
    const resizeObserver = new ResizeObserver(updateLabelWidth);
    resizeObserver.observe(labelRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [label]);

  useEffect(() => {
    if (initialValue !== value) {
      setValue(initialValue as any);
    }
  }, [initialValue]);

  const handleBlur = () => {
    setFocused(!!value);
  };

  useEffect(() => {
    if (name && setFieldValue && setFirstValue && uniqueOptions.length > 0) {
      const currentValue = formikField?.[0].value;
      if (!currentValue) {
        setFieldValue(name, uniqueOptions[0].value);
      }
    }
  }, [uniqueOptions, setFirstValue, name, setFieldValue]);

  const handleChange = (
    newValue: MultiValue<OptionType> | SingleValue<OptionType> | null,
    actionMeta: ActionMeta<OptionType>,
  ) => {
    if (onchange) {
      onchange(newValue, actionMeta);
      setFocused(!!newValue);
      return;
    }
    if (setFieldValue && name) {
      if (isMulti) {
        setFieldValue(
          name,
          (newValue as MultiValue<OptionType>).map((option) => option.value),
        );
      } else {
        setFieldValue(
          name,
          (newValue as SingleValue<OptionType>)?.value != null
            ? (newValue as SingleValue<OptionType>)?.value
            : '',
        );
      }
      setFocused(!!newValue);
      return;
    }
    setValue(newValue);
    setFocused(!!newValue);
  };

  const selectedValue = React.useMemo(() => {
    const currentValue = formikField?.[0].value ?? value;

    if (
      currentValue === null ||
      currentValue === undefined ||
      currentValue === ''
    ) {
      return null;
    }

    if (isMulti) {
      return uniqueOptions.filter((option) => {
        return Array.isArray(currentValue)
          ? currentValue.some((v) => v == option.value)
          : Array.isArray(value)
            ? (value as MultiValue<OptionType>).some(
                (v) => v?.value == option.value,
              )
            : false;
      });
    }

    return (
      uniqueOptions.find((option) => option?.value == currentValue) || null
    );
  }, [uniqueOptions, formikField?.[0].value, value, isMulti]);

  const CustomClearIndicator = (props: any) => {
    if (isMulti) {
      const selectedOptions = props.getValue() as MultiValue<OptionType>;
      const hasValues = selectedOptions && selectedOptions.length > 0;

      if (!hasValues) {
        return null;
      }

      return (
        <div
          className="minthr-select__clear-indicator"
          onClick={(e) => {
            e.stopPropagation();
            props.clearValue();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--minthr-color-danger)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--minthr-color-light-gray10)';
          }}
          title="Clear all"
        >
          <svg
            height="20"
            width="20"
            viewBox="0 0 20 20"
            aria-hidden="true"
            focusable="false"
            fill="currentColor"
          >
            <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
          </svg>
        </div>
      );
    }
    return (
      <components.ClearIndicator
        {...props}
        innerProps={{
          ...props.innerProps,
          onMouseDown: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
          },
          onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            props.clearValue();
          },
        }}
      />
    );
  };

  const CustomValueContainer = ({
    children,
    ...props
  }: ValueContainerProps<OptionType, true>) => {
    const selectedOptions = props.getValue() as MultiValue<OptionType>;
    const count = selectedOptions.length;

    const getDisplayText = () => {
      if (count === 0) return '';
      if (count === 1) return selectedOptions[0].label;
      if (count === 2) {
        return (
          <>
            <span className="minthr-select__label-item">
              {selectedOptions[0].label}
            </span>
            <span className="minthr-select__label-separator">, </span>
            <span className="minthr-select__label-item">
              {selectedOptions[1].label}
            </span>
          </>
        );
      }
      if (count >= 3) {
        const remaining = count - 2;
        return (
          <>
            <span className="minthr-select__label-item">
              {selectedOptions[0].label}
            </span>
            <span className="minthr-select__label-separator">, </span>
            <span className="minthr-select__label-item">
              {selectedOptions[1].label}
            </span>

            <span className="minthr-select__label-separator">,</span>
            <span className="minthr-select__label-count"> +{remaining}</span>
          </>
        );
      }
      return '';
    };

    return (
      <components.ValueContainer {...props}>
        {count === 0 ? (
          children
        ) : (
          <>
            <div className="minthr-select__single-value minthr-select__multi-display">
              {getDisplayText()}
            </div>
            {React.Children.toArray(children).filter(
              (child) =>
                React.isValidElement(child) &&
                child.type !== components.MultiValue,
            )}
          </>
        )}
      </components.ValueContainer>
    );
  };

  const CustomMultiValue = (props: MultiValueProps<OptionType>) => {
    return null;
  };

  const CustomOption = (props: OptionProps<OptionType, true>) => {
    return (
      <components.Option {...props}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'calc(var(--unit-minthr) * 8)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {props.label}
          </span>
        </div>
      </components.Option>
    );
  };

  const CustomMenuList = (props: MenuListProps<OptionType, true>) => {
    if (!isMulti) {
      return (
        <components.MenuList {...props}>{props.children}</components.MenuList>
      );
    }

    const selectedOptions = props.getValue() as MultiValue<OptionType>;
    const selectedValues = new Set(selectedOptions.map((opt) => opt.value));

    const children = React.Children.toArray(props.children);
    const selectedChildren: React.ReactNode[] = [];
    const unselectedChildren: React.ReactNode[] = [];

    children.forEach((child) => {
      if (React.isValidElement(child) && child.props?.data) {
        if (selectedValues.has(child.props.data.value)) {
          selectedChildren.push(child);
        } else {
          unselectedChildren.push(child);
        }
      } else {
        unselectedChildren.push(child);
      }
    });

    return (
      <components.MenuList {...props} innerRef={menuListRef}>
        {selectedChildren.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'calc(var(--unit-minthr) * 4)',
            }}
          >
            {selectedChildren}
          </div>
        )}
        {selectedChildren.length > 0 && unselectedChildren.length > 0 && (
          <div
            className="minthr-select__divider"
            style={{
              height: 'calc(var(--unit-minthr) * 1)',
              backgroundColor: 'var(--color-primary-opacity)',
              margin: 'calc(var(--unit-minthr) * 8) 0',
            }}
          />
        )}
        {unselectedChildren}
      </components.MenuList>
    );
  };

  return (
    <>
      <div
        hidden={hidden}
        className={`minthr-form-outline   ${meta && meta.touched && meta.error ? 'error' : ''}  ${className}`}
      >
        <Select
          components={{
            ...(Control ? { Control } : {}),
            ClearIndicator: CustomClearIndicator,
            ...(isMulti
              ? {
                  ValueContainer: CustomValueContainer,
                  MultiValue: CustomMultiValue,
                  ClearIndicator: CustomClearIndicator,
                  MenuList: CustomMenuList,
                  Option: CustomOption,
                }
              : {}),
          }}
          placeholder={focused ? t(placeholder) : ''}
          isSearchable={isSearchable}
          noOptionsMessage={() => t('NoData.Empty')}
          isMulti={isMulti}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          onChange={handleChange}
          options={uniqueOptions}
          className={`minthr-select ${borderNone ? '' : 'minthr-form-control'}  ${(borderNone && initialValue?.value) || (label && initialValue?.value) || selectedValue != null || isPanelOpen ? 'active' : ''} ${selectClass}`}
          value={selectedValue}
          name={name}
          isDisabled={IsDisabled}
          menuPortalTarget={appendToBody ? document.body : undefined}
          menuPlacement={placement}
          onMenuOpen={() => {
            setIsOpenPanel(true);
          }}
          onMenuClose={() => {
            setIsOpenPanel(false);
            setFocused(false);
          }}
          isClearable={isMulti || isClearable}
          classNamePrefix={'minthr-select'}
          classNames={{
            dropdownIndicator: ({ selectProps }) =>
              selectProps.menuIsOpen
                ? 'minthr-dropdown-indicator-open'
                : 'minthr-dropdown-indicator-closed',
          }}
          hideSelectedOptions={false}
          styles={{
            control: (base) => ({
              ...base,
              ...Selectstyle,
              zIndex: 1,
            }),
            valueContainer: (provided) => ({
              ...provided,
              maxHeight: isMulti ? 'calc(var(--unit-minthr) * 47)' : 'auto',
              overflowY: 'visible',
              flexWrap: 'nowrap',
              gap: 'calc(var(--unit-minthr) * 4)',
              padding:
                'calc(var(--unit-minthr) * 0) calc(var(--unit-minthr) * 18)',
            }),
            multiValue: (provided) => ({
              ...provided,
              display: 'none',
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: isMulti
                ? state.isFocused
                  ? 'var(--color-primary-opacity)'
                  : 'transparent'
                : state.isSelected
                  ? 'var(--color-primary)'
                  : state.isFocused
                    ? 'var(--color-primary-opacity)'
                    : 'transparent',
              color: isMulti
                ? 'inherit'
                : state.isSelected
                  ? '#fff'
                  : 'inherit',
              fontWeight: isMulti && state.isSelected ? '500' : 'normal',
              position: 'relative',
              ':before':
                isMulti && state.isSelected
                  ? {
                      content: '"✓"',
                      position: 'absolute',
                      left: 'calc(var(--unit-minthr) * 12)',
                      fontWeight: 'bold',
                      color: 'var(--color-primary)',
                    }
                  : {},
              paddingLeft:
                isMulti && state.isSelected
                  ? 'calc(var(--unit-minthr) * 32)'
                  : provided.paddingLeft,
              ':active': {
                backgroundColor: isMulti
                  ? 'var(--color-primary)'
                  : state.isSelected
                    ? 'var(--color-primary)'
                    : 'var(--color-primary-opacity)',
                color: isMulti ? '#fff' : 'black',
              },
            }),
          }}
        />
        {label && (
          <label ref={labelRef} className="minthr-form-label">
            {label}
          </label>
        )}
        {meta && meta.touched && meta.error ? (
          <div className="minthr-form-message">
            <span className="field-validation-error">
              <span>{t(meta.error)}</span>
            </span>
          </div>
        ) : null}
        <div className="minthr-form-notch">
          <div
            className={`minthr-form-notch-leading ${borderNone && 'minthr-border-none'}`}
          ></div>
          <div
            className={`minthr-form-notch-middle ${borderNone && 'minthr-border-none'}`}
            style={{ minWidth: `${labelWidth + (label ? 20 : 0)}px` }}
          ></div>
          <div
            className={`minthr-form-notch-trailing ${borderNone && 'minthr-border-none'}`}
          ></div>
        </div>
      </div>
    </>
  );
};

export default MinthrSelect;
